import { collection, doc, onSnapshot } from 'firebase/firestore';
import { SETTINGS_BRANDING_DOC_ID } from '../constants/workspaceBranding';

/** Settings doc id for user preferences — not a membership mirror. */
export const SETTINGS_PREFERENCES_DOC_ID = 'preferences';

const RESERVED_SETTINGS_DOC_IDS = new Set([SETTINGS_PREFERENCES_DOC_ID, SETTINGS_BRANDING_DOC_ID]);

export const MEMBERSHIP_MIRROR_KIND = 'collaboratorMembership';

/** Canonical: users/{memberUid}/settings/{ownerUid} */
export function membershipMirrorRef(db, memberUid, ownerUid) {
  return doc(db, 'users', memberUid, 'settings', ownerUid);
}

/** Legacy root subcollection (read fallback until migration). */
export function membershipMirrorLegacyRef(db, memberUid, ownerUid) {
  return doc(db, 'users', memberUid, 'memberships', ownerUid);
}

export function membershipMirrorSettingsCollection(db, memberUid) {
  return collection(db, 'users', memberUid, 'settings');
}

export function isMembershipMirrorDoc(snap) {
  if (!snap?.exists?.()) return false;
  if (RESERVED_SETTINGS_DOC_IDS.has(snap.id)) return false;
  const data = snap.data?.() ?? snap.data;
  if (!data || typeof data !== 'object') return false;
  if (data.mirrorKind === MEMBERSHIP_MIRROR_KIND) return true;
  return typeof data.ownerUid === 'string' && data.ownerUid.length > 0;
}

export function membershipDocsFromSettingsSnapshot(snap) {
  if (!snap?.docs) return [];
  return snap.docs.filter(isMembershipMirrorDoc);
}

/** Listen for owner email on a collaborator mirror (settings doc, legacy subcollection fallback). */
export function subscribeMembershipMirrorOwnerEmail(db, memberUid, ownerUid, onEmail) {
  if (!memberUid || !ownerUid) {
    onEmail(null);
    return () => {};
  }
  const canonicalRef = membershipMirrorRef(db, memberUid, ownerUid);
  const legacyRef = membershipMirrorLegacyRef(db, memberUid, ownerUid);

  const apply = (snap) => {
    const email = snap?.exists?.() ? snap.data()?.ownerEmail : null;
    onEmail(typeof email === 'string' && email.trim() ? email.trim() : null);
  };

  let legacyUnsub = null;
  const unsubCanonical = onSnapshot(
    canonicalRef,
    (snap) => {
      if (snap.exists()) {
        apply(snap);
        return;
      }
      if (!legacyUnsub) {
        legacyUnsub = onSnapshot(legacyRef, apply, () => onEmail(null));
      }
    },
    () => onEmail(null),
  );

  return () => {
    unsubCanonical();
    if (legacyUnsub) legacyUnsub();
  };
}
